import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { StatusCode } from "api/protocols";
import { GetActiveStatusBanners } from "api/rpc/2024-04/guest/statusBanner";
import { IPublicStatusBanner, StatusBannerSurface } from "api/rpc/2024-04/masterAdmin/statusBanner";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const DISMISSED_KEY_PREFIX = "status-banner-dismissed:";

function dismissedStorageKey(id: number): string {
  return `${DISMISSED_KEY_PREFIX}${id}`;
}

function isDismissedInStorage(id: number): boolean {
  try {
    return window.localStorage.getItem(dismissedStorageKey(id)) === "1";
  } catch {
    return false;
  }
}

function persistDismissed(id: number): void {
  try {
    window.localStorage.setItem(dismissedStorageKey(id), "1");
  } catch {
    // Ignore private-mode / storage quota failures; in-memory dismiss still applies.
  }
}

/**
 * Loads active status banners for a surface and keeps dismissals in localStorage.
 */
export function useStatusBanners(surface: StatusBannerSurface) {
  const [banners, setBanners] = useState<IPublicStatusBanner[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const source = axios.CancelToken.source();

    async function load() {
      const response = await GetActiveStatusBanners({ surface }, false, source.token);
      if (source.token.reason) {
        return;
      }
      if (response.status === StatusCode.OK && Array.isArray(response.data)) {
        setBanners(response.data);
      }
    }

    void load();
    const interval = window.setInterval(() => void load(), POLL_INTERVAL_MS);

    return () => {
      source.cancel();
      window.clearInterval(interval);
    };
  }, [surface]);

  const visibleBanners = useMemo(
    () =>
      banners.filter(banner => {
        if (!banner.is_dismissible) {
          return true;
        }
        return !dismissedIds.has(banner.id) && !isDismissedInStorage(banner.id);
      }),
    [banners, dismissedIds],
  );

  const dismiss = useCallback((id: number) => {
    persistDismissed(id);
    setDismissedIds(previous => {
      const next = new Set(previous);
      next.add(id);
      return next;
    });
  }, []);

  return { banners: visibleBanners, dismiss };
}
